import React, { useEffect, useMemo, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import classNames from 'classnames'
import PlusIcon from '@rsuite/icons/Plus'
import TrashIcon from '@rsuite/icons/Trash'
import CheckRoundIcon from '@rsuite/icons/CheckRound'
import WarningRoundIcon from '@rsuite/icons/WarningRound'
import { Button, Modal } from 'shared'
import { CorrectionValues } from '../types'
import { formatAmount, parseAmount } from '../utils'

type Props = {
  initial: CorrectionValues
  saving: boolean
  /** Field errors from the server, keyed like `supplier` or `lineItems[0].paid_amount`. */
  serverErrors?: Record<string, string>
  onSubmit: (values: CorrectionValues) => void
  onCancel: () => void
}

type FieldSpec = {
  key: string
  label: string
  wide?: boolean
  multiline?: boolean
}

const SECTIONS: { title: string; fields: FieldSpec[] }[] = [
  {
    title: 'Supplier',
    fields: [
      { key: 'supplier', label: 'Supplier name' },
      { key: 'supplier_no', label: 'Supplier no.' },
      { key: 'supplier_email', label: 'Email' },
      { key: 'supplier_bank_name', label: 'Bank' },
      { key: 'supplier_address', label: 'Address', wide: true, multiline: true }
    ]
  },
  {
    title: 'Payer',
    fields: [
      { key: 'payer', label: 'Payer name' },
      { key: 'payer_abn', label: 'ABN' },
      { key: 'payer_email', label: 'Email' },
      { key: 'payer_address', label: 'Address', wide: true, multiline: true }
    ]
  },
  {
    title: 'Payment',
    fields: [
      { key: 'payment_reference', label: 'Payment reference' },
      { key: 'payment_date', label: 'Payment date' },
      { key: 'total_amount', label: 'Total amount' },
      { key: 'doc_label', label: 'Document label' }
    ]
  }
]

const LINE_COLUMNS: { key: string; label: string; numeric?: boolean }[] = [
  { key: 'invoice_number', label: 'Invoice no.' },
  { key: 'invoice_date', label: 'Invoice date' },
  { key: 'gross_amount', label: 'Gross', numeric: true },
  { key: 'paid_amount', label: 'Paid', numeric: true }
]

const emptyLine = (): Record<string, string> =>
  Object.fromEntries(LINE_COLUMNS.map((c) => [c.key, '']))

const TOLERANCE = 0.01

/** Checks the pipeline would also make, so problems show before sending. */
export const validateCorrection = (values: CorrectionValues) => {
  const errors: Record<string, string> = {}
  const { fields } = values
  if (!fields.supplier?.trim() && !fields.payer?.trim()) {
    errors.supplier = 'Enter the supplier or the payer'
  }
  if (fields.total_amount?.trim() && parseAmount(fields.total_amount) == null) {
    errors.total_amount = 'Enter an amount like 1,234.56'
  }
  const lines = values.lineItems.filter((line) =>
    Object.values(line).some((v) => v?.trim())
  )
  if (!lines.length) errors.lineItems = 'Add at least one invoice line'
  values.lineItems.forEach((line, i) => {
    if (!Object.values(line).some((v) => v?.trim())) return
    const at = `lineItems[${i}]`
    if (!line.invoice_number?.trim()) {
      errors[`${at}.invoice_number`] = 'Required'
    }
    if (!line.gross_amount?.trim() && !line.paid_amount?.trim()) {
      errors[`${at}.paid_amount`] = 'Enter gross or paid'
    }
    ;['gross_amount', 'paid_amount'].forEach((key) => {
      if (line[key]?.trim() && parseAmount(line[key]) == null) {
        errors[`${at}.${key}`] = 'Not an amount'
      }
    })
  })
  return errors
}

/**
 * Review form for a remittance Document AI couldn't process cleanly: header
 * fields, editable invoice lines and a live check that the lines add up to
 * the total. Submitting sends it through the normal validation and delivery.
 */
const CorrectionForm: React.FC<Props> = ({
  initial,
  saving,
  serverErrors,
  onSubmit,
  onCancel
}) => {
  const [values, setValues] = useState<CorrectionValues>(() => ({
    fields: { ...initial.fields },
    lineItems: initial.lineItems.length
      ? initial.lineItems.map((l) => ({ ...emptyLine(), ...l }))
      : [emptyLine()]
  }))
  const [showErrors, setShowErrors] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const dirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initial),
    [values, initial]
  )
  const localErrors = useMemo(() => validateCorrection(values), [values])
  const errors = { ...(showErrors ? localErrors : {}), ...(serverErrors ?? {}) }

  // Keep unsaved corrections from being lost by navigating away.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !saving && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (!dirty) return undefined
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const setField = (key: string, value: string) =>
    setValues((v) => ({ ...v, fields: { ...v.fields, [key]: value } }))
  const setCell = (row: number, key: string, value: string) =>
    setValues((v) => ({
      ...v,
      lineItems: v.lineItems.map((line, i) =>
        i === row ? { ...line, [key]: value } : line
      )
    }))
  const addLine = () =>
    setValues((v) => ({ ...v, lineItems: [...v.lineItems, emptyLine()] }))
  const removeLine = (row: number) =>
    setValues((v) => ({
      ...v,
      lineItems:
        v.lineItems.length > 1
          ? v.lineItems.filter((_, i) => i !== row)
          : [emptyLine()]
    }))

  // Live reconciliation: paid (or gross) per line against the total.
  const total = parseAmount(values.fields.total_amount)
  const linesTotal = values.lineItems.reduce((sum, line) => {
    const amount =
      parseAmount(line.paid_amount) ?? parseAmount(line.gross_amount)
    return amount == null ? sum : sum + amount
  }, 0)
  const balanced = total != null && Math.abs(total - linesTotal) < TOLERANCE

  const requestSubmit = () => {
    setShowErrors(true)
    if (Object.keys(localErrors).length === 0) {
      setConfirmOpen(true)
      return
    }
    // Take the reviewer to the first problem, which may be off-screen.
    setTimeout(() => {
      const first = document.querySelector<HTMLElement>(
        '.dx-correct [aria-invalid="true"]'
      )
      first?.focus()
      first?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }

  const fieldError = (key: string) => errors[key]

  return (
    <div className="dx-correct">
      {SECTIONS.map((section) => (
        <section key={section.title} className="dx-section">
          <h3 className="dx-section__title">{section.title}</h3>
          <div className="dx-form-grid">
            {section.fields.map((field) => {
              const id = `dx-field-${field.key}`
              const error = fieldError(field.key)
              const changed =
                (values.fields[field.key] ?? '') !==
                (initial.fields[field.key] ?? '')
              return (
                <div
                  key={field.key}
                  className={classNames('dx-form-field', {
                    'dx-form-field--wide': field.wide,
                    'dx-form-field--error': error,
                    'dx-form-field--changed': changed
                  })}
                >
                  <label htmlFor={id}>{field.label}</label>
                  {field.multiline ? (
                    <textarea
                      id={id}
                      rows={3}
                      value={values.fields[field.key] ?? ''}
                      onChange={(e) => setField(field.key, e.target.value)}
                      aria-invalid={!!error}
                    />
                  ) : (
                    <input
                      id={id}
                      value={values.fields[field.key] ?? ''}
                      onChange={(e) => setField(field.key, e.target.value)}
                      aria-invalid={!!error}
                    />
                  )}
                  {error && <span className="dx-form-error">{error}</span>}
                </div>
              )
            })}
          </div>
        </section>
      ))}

      <section className="dx-section">
        <h3 className="dx-section__title">
          Invoice lines
          <span className="dx-count">{values.lineItems.length}</span>
        </h3>
        {errors.lineItems && (
          <p className="dx-form-error dx-form-error--block">
            {errors.lineItems}
          </p>
        )}
        <div className="dx-table-wrap dx-table-wrap--inner">
          <table className="dx-table dx-table--compact dx-table--edit">
            <thead>
              <tr>
                <th scope="col" className="dx-num">
                  #
                </th>
                {LINE_COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    className={classNames({ 'dx-num': c.numeric })}
                  >
                    {c.label}
                  </th>
                ))}
                <th scope="col">
                  <span className="dx-sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {values.lineItems.map((line, row) => (
                // Lines have no identity of their own; position is the key.
                // eslint-disable-next-line react/no-array-index-key
                <tr key={row}>
                  <td className="dx-num dx-muted">{row + 1}</td>
                  {LINE_COLUMNS.map((c) => {
                    const error = fieldError(`lineItems[${row}].${c.key}`)
                    return (
                      <td key={c.key}>
                        <input
                          className={classNames('dx-cell-input', {
                            'dx-num': c.numeric,
                            'dx-cell-input--error': error
                          })}
                          value={line[c.key] ?? ''}
                          onChange={(e) => setCell(row, c.key, e.target.value)}
                          aria-label={`${c.label}, line ${row + 1}`}
                          aria-invalid={!!error}
                          title={error}
                        />
                      </td>
                    )
                  })}
                  <td className="dx-table__actions">
                    <button
                      type="button"
                      className="dx-icon-btn"
                      onClick={() => removeLine(row)}
                      aria-label={`Remove line ${row + 1}`}
                      title="Remove line"
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" className="dx-link-btn" onClick={addLine}>
          <PlusIcon /> Add line
        </button>

        {total != null && (
          <div
            className={classNames('dx-check', {
              'dx-check--ok': balanced,
              'dx-check--warn': !balanced
            })}
          >
            {balanced ? <CheckRoundIcon /> : <WarningRoundIcon />}
            <div>
              <p className="dx-check__title">
                {balanced
                  ? 'Lines add up to the total'
                  : 'Lines don’t add up to the total'}
              </p>
              <p className="dx-check__body">
                Total {formatAmount(total)} · Lines {formatAmount(linesTotal)}
                {!balanced &&
                  ` · Difference ${formatAmount(total - linesTotal)}`}
              </p>
            </div>
          </div>
        )}
      </section>

      <div className="dx-savebar" role="region" aria-label="Save corrections">
        <span className="dx-savebar__state">
          {dirty ? 'Unsaved changes' : 'No changes yet'}
        </span>
        <Button appearance="subtle" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          appearance="primary"
          className="dx-btn-primary"
          loading={saving}
          onClick={requestSubmit}
        >
          Save &amp; send
        </Button>
      </div>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        size="xs"
        title="Save and send?"
        dialogClassName="dx-modal"
        body={
          <p className="dx-modal-text">
            Your corrections replace what Document AI read. They go through the
            usual checks and, if they pass, are sent to the connected system —
            this can’t be undone here.
          </p>
        }
        footer={
          <div className="dx-modal-footer">
            <Button appearance="subtle" onClick={() => setConfirmOpen(false)}>
              Keep editing
            </Button>
            <Button
              appearance="primary"
              className="dx-btn-primary"
              onClick={() => {
                setConfirmOpen(false)
                onSubmit(values)
              }}
            >
              Save &amp; send
            </Button>
          </div>
        }
      />

      <Modal
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        size="xs"
        title="Discard your corrections?"
        dialogClassName="dx-modal"
        body={
          <p className="dx-modal-text">
            You have changes that haven’t been sent. Leaving this page loses
            them.
          </p>
        }
        footer={
          <div className="dx-modal-footer">
            <Button appearance="subtle" onClick={() => blocker.reset?.()}>
              Keep editing
            </Button>
            <Button
              appearance="primary"
              className="dx-btn-primary"
              onClick={() => blocker.proceed?.()}
            >
              Discard
            </Button>
          </div>
        }
      />
    </div>
  )
}

export default CorrectionForm
